import React from 'react';
import { Car, Lock, LockOpen, Pencil, Trash2, ArrowUpRight } from 'lucide-react';
import TripLink from '../common/TripLink';
import SourceDocumentLinks from './SourceDocumentLinks';
import StickyNote from './StickyNote';
import { ClockTime } from './TimelineCardVisuals';

// Building blocks shared by the trip timeline "Cards" view: the hotel stay
// cards and the per-type cards (flights, rides, meals, meetings, events...).

export const MONO = { fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace" };

// Timeline times are wall-clock values at the place; read them as written.
export const wallClock = (raw) => {
  const m = String(raw || '').match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
  return m ? { date: `${m[1]}-${m[2]}-${m[3]}`, time: m[4] ? `${m[4]}:${m[5]}` : null } : null;
};

// "HH:mm" in the user's time format (12-hour unless they chose 24H)
export const formatClock = (time, prefs = {}) => {
  if (!time || prefs.time_view_format === '24H') return time;
  const [hour, minute] = time.split(':').map(Number);
  return `${hour % 12 || 12}:${String(minute).padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}`;
};

export const formatDay = (date, withYear = false) => (date
  ? new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', ...(withYear && { year: 'numeric' }) })
    .format(new Date(`${date}T12:00:00Z`))
  : 'Date TBD');

// "2h 35m" between two wall-clock datetimes, or null
export const durationLabel = (startRaw, endRaw) => {
  const s = wallClock(startRaw);
  const e = wallClock(endRaw);
  if (!s?.time || !e?.time) return null;
  const mins = Math.round((Date.parse(`${e.date}T${e.time}:00Z`) - Date.parse(`${s.date}T${s.time}:00Z`)) / 60000);
  if (!(mins > 0)) return null;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return [h && `${h}h`, m && `${m}m`].filter(Boolean).join(' ');
};

export const present = (value) => {
  const s = value === null || value === undefined ? '' : String(value).trim();
  return s && s !== 'TBD' && s !== 'Location TBD' ? s : null;
};

const STATUS = {
  confirmed: { label: 'Confirmed', className: 'bg-ui-green-bg text-ui-green-fg', dot: 'bg-ui-green-fg' },
  pending: { label: 'Pending confirmation', className: 'bg-ui-amber-bg text-ui-amber-fg', dot: 'bg-ui-amber-fg' },
  cancelled: { label: 'Cancelled', className: 'bg-ui-red-bg text-ui-red-fg', dot: 'bg-ui-red-fg' },
};

export const Pill = ({ className, dot, children }) => (
  <span className={`inline-flex items-center gap-1.5 h-[26px] px-2.5 rounded-full text-xs font-semibold whitespace-nowrap ${className}`}>
    {dot && <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />}{children}
  </span>
);

export const StatusPill = ({ status }) => {
  if (!status) return null;
  const s = STATUS[status] || { label: status.replace(/_/g, ' '), className: 'bg-ui-hover text-ui-ink-3', dot: 'bg-ui-ink-3' };
  return <Pill className={s.className} dot={s.dot}>{s.label}</Pill>;
};

// Tailwind needs whole class names, so each tone is spelled out.
export const TONES = {
  teal: { tile: 'bg-ui-teal-bg text-ui-teal-fg', fg: 'text-ui-teal-fg', solid: 'bg-ui-teal-fg', ring: 'border-ui-teal-fg', line: 'bg-ui-teal-line' },
  violet: { tile: 'bg-ui-violet-bg text-ui-violet-fg', fg: 'text-ui-violet-fg', solid: 'bg-ui-violet-fg', ring: 'border-ui-violet-fg', line: 'bg-ui-violet-line' },
  indigo: { tile: 'bg-ui-indigo-bg text-ui-indigo-fg', fg: 'text-ui-indigo-fg', solid: 'bg-ui-indigo-fg', ring: 'border-ui-indigo-fg', line: 'bg-ui-indigo-line' },
  amber: { tile: 'bg-ui-amber-bg text-ui-amber-fg', fg: 'text-ui-amber-fg', solid: 'bg-ui-amber-fg', ring: 'border-ui-amber-fg', line: 'bg-ui-amber-line' },
  orange: { tile: 'bg-ui-orange-bg text-ui-orange-fg', fg: 'text-ui-orange-fg', solid: 'bg-ui-orange-fg', ring: 'border-ui-orange-fg', line: 'bg-ui-orange-line' },
  blue: { tile: 'bg-ui-blue-bg text-ui-blue-fg', fg: 'text-ui-blue-fg', solid: 'bg-ui-blue-fg', ring: 'border-ui-blue-fg', line: 'bg-ui-blue-line' },
  pink: { tile: 'bg-ui-pink-bg text-ui-pink-fg', fg: 'text-ui-pink-fg', solid: 'bg-ui-pink-fg', ring: 'border-ui-pink-fg', line: 'bg-ui-pink-line' },
  green: { tile: 'bg-ui-green-bg text-ui-green-fg', fg: 'text-ui-green-fg', solid: 'bg-ui-green-fg', ring: 'border-ui-green-fg', line: 'bg-ui-green-line' },
};

export const IconTile = ({ Icon, tone = 'teal' }) => (
  <div className={`w-10 h-10 rounded-[10px] flex items-center justify-center flex-none ${TONES[tone].tile}`}>
    <Icon className="w-5 h-5" aria-hidden="true" />
  </div>
);

export const Label = ({ children }) => (
  <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-ui-muted">{children}</div>
);

export const ButtonLink = ({ onClick, children, primary = false, label, href }) => {
  const className = primary
    ? 'h-10 px-4 inline-flex items-center gap-1.5 rounded-lg bg-ui-primary hover:bg-ui-primary-hover text-sm font-semibold text-ui-on-primary whitespace-nowrap'
    : 'h-9 px-3 inline-flex items-center gap-1.5 rounded-lg border border-ui-control text-[13px] text-ui-ink-2 hover:bg-ui-hover whitespace-nowrap';
  if (href) {
    return <a href={href} aria-label={label} onClick={e => e.stopPropagation()} className={className}>{children}</a>;
  }
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onClick(); }} aria-label={label} className={className}>
      {children}
    </button>
  );
};

// The card's header row: tinted icon tile, title + subtitle, and a right slot.
export const CardHeader = ({ Icon, tone, title, subtitle, children }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-[22px] py-[18px] border-b border-ui-divider">
    <div className="flex items-center gap-3.5 min-w-0">
      <IconTile Icon={Icon} tone={tone} />
      <div className="min-w-0">
        <h3 className="m-0 text-lg font-bold tracking-tight text-ui-ink break-words">{title}</h3>
        {subtitle && <div className="text-[13px] text-ui-muted mt-0.5 break-words">{subtitle}</div>}
      </div>
    </div>
    {children && <div className="flex items-center gap-2">{children}</div>}
  </div>
);

export const CardShell = ({ label, children }) => (
  <article className="bg-ui-surface border border-ui-line rounded-2xl overflow-hidden shadow-ui-card" aria-label={label}>
    {children}
  </article>
);

// The connector between two ends of a card: dot — line — icon — line — ring.
export const RouteLine = ({ tone, Icon, caption, iconClassName = '' }) => (
  <div className="flex flex-col items-center gap-1.5 w-16 sm:w-[140px]">
    <div className="flex items-center w-full gap-1.5" aria-hidden="true">
      <span className={`w-2.5 h-2.5 rounded-full flex-none ${TONES[tone].solid}`} />
      <span className={`flex-1 h-0.5 ${TONES[tone].line}`} />
      {Icon && <Icon className={`w-3.5 h-3.5 flex-none ${TONES[tone].fg} ${iconClassName}`} />}
      {Icon && <span className={`flex-1 h-0.5 ${TONES[tone].line}`} />}
      <span className={`w-2.5 h-2.5 rounded-full border-2 flex-none ${TONES[tone].ring}`} />
    </div>
    {caption && <span className="text-xs text-ui-muted text-center">{caption}</span>}
  </div>
);

// A big mono clock with its label above and a line below.
export const TimeBlock = ({ label, time, prefs, below, align = 'left', muted = false }) => (
  <div className={`min-w-0 ${align === 'right' ? 'text-right' : ''}`}>
    {label && <Label>{label}</Label>}
    <div className={`text-[22px] sm:text-[26px] font-semibold mt-1.5 ${muted ? 'text-ui-ink-3' : 'text-ui-ink'}`} style={MONO}>
      <ClockTime value={formatClock(time, prefs)} />
    </div>
    {below}
  </div>
);

// The definition grid near the foot of a card. `items` = [{ term, children }].
export const DetailGrid = ({ items }) => {
  const shown = items.filter(Boolean);
  if (!shown.length) return null;
  const cols = shown.length >= 3 ? 'sm:grid-cols-3' : shown.length === 2 ? 'sm:grid-cols-2' : '';
  return (
    <dl className={`m-0 grid grid-cols-1 ${cols} gap-4 px-4 sm:px-[22px] py-4 border-t border-ui-divider`}>
      {shown.map(item => (
        <div key={item.term} className="min-w-0">
          <dt className="text-xs text-ui-muted">{item.term}</dt>
          {item.children}
        </div>
      ))}
    </dl>
  );
};

export const DetailText = ({ children, strong = false, soft = false }) => (
  <dd className={`m-0 mt-1 text-[13px] break-words ${strong ? 'font-semibold text-ui-ink' : soft ? 'text-ui-ink-3' : 'text-ui-ink'}`}>{children}</dd>
);

export const Chips = ({ names }) => (
  <dd className="m-0 mt-2 flex flex-wrap gap-1.5">
    {names.map((n, i) => (
      <span key={`${n.name}-${i}`} className={`inline-flex items-center h-[26px] px-2.5 rounded-full text-xs bg-ui-hover ${n.emphasis ? 'font-semibold text-ui-ink' : 'text-ui-ink-2'}`}>
        {n.name}
      </span>
    ))}
  </dd>
);

export const CardFooter = ({
  component, canViewTrip, confLabel = 'Conf.', confNumber, isConfidential, onOpenConfidentiality,
  onEdit, editLabel = 'Edit', editText = 'Edit', EditIcon = Pencil,
  onDelete, deleteLabel = 'Delete', deleteText = 'Delete', DeleteIcon = Trash2, actions, children,
}) => (
  <div className="flex flex-wrap items-center justify-between gap-2.5 px-4 sm:px-[22px] py-3 bg-ui-sunk border-t border-ui-divider">
    <div className="flex items-center gap-1 min-w-0">
      {canViewTrip && component.tripId && (
        <TripLink
          to={`/Trip/${component.tripId}`}
          onClick={e => e.stopPropagation()}
          className="inline-flex items-center gap-1 min-w-0 text-[13px] text-ui-link hover:text-ui-blue-fg"
          aria-label={`View trip timeline: ${component.tripTitle || 'Trip'}`}
        >
          <span className="truncate">{component.tripTitle || 'Trip timeline'}</span>
          <ArrowUpRight className="w-3.5 h-3.5 flex-none" aria-hidden="true" />
        </TripLink>
      )}
      <SourceDocumentLinks component={component} />
      {onOpenConfidentiality && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpenConfidentiality(); }}
          aria-label={isConfidential ? 'Confidential: change visibility' : 'Mark confidential'}
          title={isConfidential ? 'Confidential' : 'Mark confidential'}
          className={`w-9 h-9 inline-flex items-center justify-center rounded-lg ${isConfidential ? 'bg-ui-amber-bg text-ui-amber-fg' : 'text-ui-muted hover:text-ui-ink hover:bg-ui-hover'}`}
        >
          {isConfidential ? <Lock className="w-4 h-4" /> : <LockOpen className="w-4 h-4" />}
        </button>
      )}
      {children}
      {confNumber && <span className="text-[13px] text-ui-ink-3 truncate">{confLabel} <span className="text-ui-ink" style={MONO}>{confNumber}</span></span>}
    </div>
    {(actions || onEdit || onDelete) && (
      <div className="flex items-center gap-2">
        {actions}
        {onEdit && <ButtonLink onClick={onEdit} label={editLabel}><EditIcon className="w-3.5 h-3.5" aria-hidden="true" />{editText}</ButtonLink>}
        {onDelete && <ButtonLink onClick={onDelete} label={deleteLabel}><DeleteIcon className="w-3.5 h-3.5" aria-hidden="true" />{deleteText}</ButtonLink>}
      </div>
    )}
  </div>
);

export const InsideNotes = ({ notes = [], noteHandlers = {}, currentUserId }) => (notes.length > 0 ? (
  <div className="flex flex-col gap-2 px-4 sm:px-[22px] pb-4">
    {notes.map(note => (
      <StickyNote key={note.id} note={note} currentUserId={currentUserId} onToggleExpand={noteHandlers.onToggleNoteExpand} onEdit={noteHandlers.onEditNote} onDelete={noteHandlers.onDeleteNote} />
    ))}
  </div>
) : null);

export const TransferBooked = ({ booking, title }) => (
  <div className="flex items-center gap-3 px-3.5 py-3 rounded-[10px] bg-ui-green-bg border border-ui-green-line">
    <Car className="w-4 h-4 flex-none text-ui-green-fg" aria-hidden="true" />
    <div className="min-w-0">
      <div className="text-sm font-semibold text-ui-ink">
        {title}{wallClock(booking.pickup_datetime)?.time && <> · <span style={MONO}>{wallClock(booking.pickup_datetime).time}</span></>}
      </div>
      <div className="text-[13px] text-ui-ink-3 truncate">
        {[`${booking.pickup_location || 'Pickup'} → ${booking.dropoff_location || 'Drop-off'}`, booking.vehicle_class, booking.urcn && `Ride ${booking.urcn}`].filter(Boolean).join(' · ')}
      </div>
    </div>
  </div>
);

export const NoRideStrip = ({ text, onBook }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 px-3.5 py-2.5 rounded-[10px] bg-ui-blue-bg border border-ui-blue-line">
    <span className="flex items-center gap-2.5 text-[13px] text-ui-blue-fg min-w-0">
      <Car className="w-4 h-4 flex-none" aria-hidden="true" />
      <span>{text}</span>
    </span>
    <ButtonLink onClick={onBook}>Book ride</ButtonLink>
  </div>
);

// A tinted information strip (e.g. "Next: ..." or "No ride needed").
export const InfoStrip = ({ Icon, title, detail, tone = 'blue' }) => {
  const box = tone === 'green' ? 'bg-ui-green-bg border-ui-green-line' : 'bg-ui-blue-bg border-ui-blue-line';
  const fg = tone === 'green' ? 'text-ui-green-fg' : 'text-ui-blue-fg';
  return (
    <div className={`flex items-center gap-3 px-3.5 py-3 rounded-[10px] border ${box}`}>
      {Icon && <Icon className={`w-4 h-4 flex-none ${fg}`} aria-hidden="true" />}
      <div className="min-w-0">
        <div className="text-sm font-semibold text-ui-ink">{title}</div>
        {detail && <div className="text-[13px] text-ui-ink-3">{detail}</div>}
      </div>
    </div>
  );
};

// Stack of ride strips under a card's main row.
export const RideSection = ({ children }) => {
  const shown = React.Children.toArray(children).filter(Boolean);
  return shown.length ? <div className="flex flex-col gap-2 px-4 sm:px-[22px] pb-[18px]">{shown}</div> : null;
};
